import get from 'lodash-es/get'
import findIndex from 'lodash-es/findIndex'
import isEmpty from 'lodash-es/isEmpty'
import isNil from 'lodash-es/isNil'
import omit from 'lodash-es/omit'
import omitBy from 'lodash-es/omitBy'
import uniqBy from 'lodash-es/uniqBy'
import pick from 'lodash-es/pick'
import keyBy from 'lodash-es/keyBy'
import some from 'lodash-es/some'

import Util from 'components/DrawingCanvas/lib/util'
import Units from 'components/DrawingCanvas/lib/units'
import { objectClassNameFromStateKey, objectStateKeyFromClassName } from './selectors'
import shiftGridLines from './shiftGridLines'
import LAYER_KEYS, { LayerKeys } from 'config/layerKeys'
import CLASS_NAMES, { ObjectClassNamesToStateTypes } from 'config/objectClassNames'
import type * as Types from './types'
import { produce } from 'immer'

import {
  loadFacility,
  resetFacility,
  addObjectAction,
  updateObjectAction,
  updateObjectsAction,
  updateObjectsPassively,
  deleteObjectsAction,
  duplicateObjectsAction,
  duplicateGrid,
  updateRoofAction,
  updateRoofStructure as updateRoofStructureAction,
  updateRoofSectionAction,
  addMountingBeam,
  removeMountingBeam,
  updateMountingBeam,
  addColumnLine,
  removeColumnLine,
  updateColumnLine,
  revertRoofSections,
  addElevationPoint,
  updateElevationPointAction,
  addElevationLine,
  updateElevationLineAction,
  updateWallAction,
  updateSegmentsAction,
  addDoor,
  updateDoor,
  addUtilityBoxAction,
  updateUtilityBox,
  addProductAction,
  distributeProductsAction,
  updateProductAction,
  addObstructionAction,
  distributeObstructionsAction,
  distributeDoors,
  updateObstructionAction,
  addComfortZone,
  updateComfortZone,
  addDimensionAction,
  updateDimension,
  addCeilingAction,
  updateCeilingAction,
  deleteCeilingAction,
  requestAirflow,
  receiveAirflow,
  updateAirflowStatus,
  setAirflowLayer,
  addBackgroundImage,
  deleteBackgroundImage,
  updateBackgroundImage,
  addMetadataImage,
  requestHeatMap,
  receiveHeatMap,
  updateHeatMapStatus,
  setHeatMapLayer,
  RoofStructure,
} from './actions'
import * as CFD from '../cfd/actions'
import { toggleLayerVisibility } from '../layers'

import { UnknownAction } from 'redux'
import { magnitude, slidingWindow, sub } from '~/lib/segment-patchup'
import { Vector3, type Vector3Tuple } from 'three'
import { RootState } from '..'
import { primaryMountingTypes, primaryToSecondaryTypes, structureStyles } from '~/config/mountingStructures'
import theme from '~/config/theme'
import { getMountingStructurePositions } from '~/components/DrawingCanvas/MountingStructures/util'
import { getBeamOrientation, getOrthoOrientation } from '~/components/Panels/SelectedRoofSectionPanel/util'
import utilityBoxReducer from './utilityBoxReducer'
import { getPerimeterPoints } from '~/components/DrawingCanvas/Walls/util'

function updateWall(wall: Types.Wall | Types.DenormalizedWall, state: ObjectsState): ObjectsState {
  let segmentObjects: Types.WallSegment[]

  if (wall.segments.every(item => typeof item === 'string')) {
    segmentObjects = wall.segments.map(id => state.segments[id])
  } else {
    segmentObjects = wall.segments
  }

  const perimeterPoints = getPerimeterPoints(segmentObjects).map<Vector3Tuple>(({ x, y, z }) => [x, y, z])

  const roofSections: Record<string, Types.RoofSection> = {}
  wall.roofSectionIds.forEach(
    roofSectionId =>
      (roofSections[roofSectionId] = {
        ...state.roofSections[roofSectionId],
        perimeterPoints,
        position: wall.position,
      })
  )

  const segments: Record<string, Types.WallSegment> = {}
  segmentObjects.forEach(segment => {
    segments[segment.id] = {
      ...segments[segment.id],
      ...segment,
    }
  })
  const relatedRoof = wall.roofId ? state.roofs[wall.roofId] : undefined
  const updatedRoof = relatedRoof
    ? { [relatedRoof.id]: { ...relatedRoof, perimeterPoints, position: wall.position } }
    : {}
  return {
    ...state,
    objects: {
      ...state.objects,
      [wall.id]: {
        ...state.objects[wall.id],
        ...wall,
        segments: segmentObjects.map(segment => segment.id),
        metadata: {
          ...state.objects[wall.id].metadata,
          ...wall.metadata,
        },
      },
    },
    segments: {
      ...state.segments,
      ...segments,
    },
    roofs: {
      ...state.roofs,
      ...updatedRoof,
    },
    roofSections: {
      ...state.roofSections,
      ...roofSections,
    },
  }
}

function updateElevationLine(elevationLine: Types.ElevationLine, state: ObjectsState) {
  const newElevationPoints = {
    ...state.elevationPoints,
  }

  const height = elevationLine.height
  if (height !== undefined) {
    const linePointIds = elevationLine.elevationPointIds

    linePointIds.forEach(id => {
      const ep: Types.ElevationPoint = {
        ...state.elevationPoints[id],
        position: {
          ...state.elevationPoints[id].position,
          z: height,
        },
      }
      newElevationPoints[id] = ep
    })
  }

  return {
    ...state,
    needsSaved: true,
    elevationLines: {
      ...state.elevationLines,
      [elevationLine.id]: {
        ...elevationLine,
      },
    },
    elevationPoints: newElevationPoints,
  }
}

function updateSegment(segment: Types.WallSegment, state: ObjectsState) {
  // Update height of associated roof and roof sections.
  const prevSegment = state.segments[segment.id]

  const updatedSegment = {
    ...prevSegment,
    ...segment,
  }
  let newState = {
    ...state,
    segments: {
      ...state.segments,
      [updatedSegment.id]: updatedSegment,
    },
  }

  const parent = newState.objects[updatedSegment.parentId]
  const height = updatedSegment.height
  const heightDidChange = updatedSegment.height !== prevSegment.height
  const roofs = parent.roofId
    ? {
        ...newState.roofs,
        [parent.roofId]: { ...newState.roofs[parent.roofId], height },
      }
    : newState.roofs
  const roofSections: Record<string, Types.RoofSection> = {}
  parent.roofSectionIds.forEach(roofSectionId => {
    roofSections[roofSectionId] = {
      ...newState.roofSections[roofSectionId],
      height,
    }
  })

  const attachedDoors = Object.values(state.doors).filter(door => door.wallSegmentId === segment.id)
  if (attachedDoors.length) {
    const start = new Vector3().copy(segment.startPoint)
    const end = new Vector3().copy(prevSegment.startPoint)
    const delta = new Vector3().subVectors(start, end)
    const updatedDoors = attachedDoors.map(door => {
      const { x, y, z } = new Vector3().copy(door.position).add(delta)
      return [door.id, { ...door, position: { x, y, z } }]
    })
    newState = {
      ...newState,
      doors: {
        ...newState.doors,
        ...Object.fromEntries(updatedDoors),
      },
    }
  }

  if (heightDidChange) {
    const relatedCeilings = getRelatedCeilings(segment.id, newState)
    if (relatedCeilings.length === 1) {
      const ceilingId = relatedCeilings[0]
      const ceiling = {
        ...newState.ceilings[ceilingId],
        height,
      }
      newState = updateCeiling(ceiling, newState)
    }
  }

  return {
    ...newState,
    roofs: roofs,
    roofSections: {
      ...newState.roofSections,
      ...roofSections,
    },
  }
}

function getSegmentsForCeilings(state: ObjectsState) {
  const ceilings: Record<string, { segments: string[] }> = {}
  Object.values(state.ceilings).forEach(ceiling => {
    const { id, perimeterPoints } = ceiling
    const segments: string[] = []
    Object.values(state.segments).forEach(segment => {
      let matchedIndex: number | undefined
      const hasSegment = some(perimeterPoints, (point, i) => {
        // TODO: Shouldn't all units be stored/used in the same unit of measure?
        const x = +Units.nativeToInches(point.x).toPrecision(6)
        const y = +Units.nativeToInches(point.y).toPrecision(6)
        const startX = +segment.startPoint.x.toPrecision(6)
        const startY = +segment.startPoint.y.toPrecision(6)
        const endX = +segment.endPoint.x.toPrecision(6)
        const endY = +segment.endPoint.y.toPrecision(6)
        if ((x === startX && y === startY) || (x === endX && y === endY)) {
          // If an adjacent point was matched, we have a matching segment
          if (matchedIndex === (i - 1) % perimeterPoints.length || matchedIndex === (i + 1) % perimeterPoints.length) {
            return true
          } else {
            matchedIndex = i
          }
        }
      })
      if (hasSegment) {
        segments.push(segment.id)
      }
    })
    ceilings[id] = {
      segments,
    }
  })

  return ceilings
}

function getRelatedCeilings(segmentId: string, state: ObjectsState) {
  const segment = state.segments[segmentId]
  const ceilingSegments = getSegmentsForCeilings(state)
  if (segment.layerKey === LAYER_KEYS.INTERIOR_WALLS) {
    const enabledCeilings = Object.values(state.ceilings)
      .filter(ceiling => ceiling.enabled)
      .map(ceiling => ceiling.id)
    return Object.keys(ceilingSegments)
      .filter(id => enabledCeilings.includes(id))
      .filter(id => ceilingSegments[id].segments.includes(segmentId))
  }

  return []
}

function updateCeiling(object: Types.Ceiling, state: ObjectsState) {
  const curCeiling = get(state, `ceilings.${object.id}`)
  const newState = {
    ...state,
    ceilings: {
      ...state.ceilings,
      [object.id]: {
        ...state.ceilings[object.id],
        ...object,
        metadata:
          object.metadata || state.ceilings[object.id].metadata
            ? {
                ...state.ceilings[object.id].metadata,
                ...object.metadata,
              }
            : undefined,
      },
    },
  }

  // Udpdate wall segment heights iff:
  // * Ceiling is switched to enabled OR
  // * Ceiling is enabled AND ceiling height changed
  const updateWallHeights =
    (object.enabled && !curCeiling.enabled) || (object.enabled && object.height !== curCeiling.height)

  if (updateWallHeights) {
    // Update wall segment heights to match ceiling
    const updatedSegments: Record<string, Types.WallSegment> = {}
    const ceilingSegments = getSegmentsForCeilings(newState)
    ceilingSegments[object.id].segments
      .filter(segId => !newState.segments[segId].isFullHeight) // Don't update fullHeight segments
      .forEach(segId => {
        // Check if wall is shared
        const relatedCeilings = getRelatedCeilings(segId, newState)

        // If wall is not shared, update height
        if (relatedCeilings.length === 1) {
          updatedSegments[segId] = {
            ...newState.segments[segId],
            height: object.height,
          }
        }
      })
    newState.segments = {
      ...newState.segments,
      ...updatedSegments,
    }
  }

  return newState
}

function updateRoofStructure(object: RoofStructure, state: ObjectsState): ObjectsState {
  // Build new elevation points
  const elevationPoints: Record<string, Types.ElevationPoint> = {}
  object.elevationPoints.forEach(point => {
    const id = Util.guid()
    elevationPoints[id] = {
      id,
      position: point,
      roofId: object.roofId,
      wallId: object.wallId,
    }
  })

  // Get new elevation point ids
  const elevationPointIds: string[] = []
  Object.keys(elevationPoints).forEach(function (key) {
    elevationPointIds.push(elevationPoints[key].id)
  })

  // Build new elevation line
  const elevationLineId = Util.guid()
  const elevationLine: Types.ElevationLine = {
    id: elevationLineId,
    elevationPointIds,
    roofId: object.roofId,
    height: object.height,
  }

  return {
    ...state,
    elevationPoints: {
      ...elevationPoints,
    },
    elevationLines: {
      [elevationLineId]: elevationLine,
    },
    roofs: {
      [object.roofId]: {
        ...state.roofs[object.roofId],
        elevationLineIds: [elevationLineId],
        elevationPointIds: [],
      },
    },
  }
}

function findObjectById(
  state: ObjectsState,
  id: string
): [Types.StateObject, keyof ObjectsState] | [undefined, undefined] {
  for (const key_ in state) {
    const key = key_ as keyof ObjectsState
    if (state[key] && typeof state[key] === 'object' && id in state[key]) {
      return [(state as any)[key][id], key]
    }
  }
  return [undefined, undefined]
}

function updateObject(object: Types.PartialObject<Types.StateObject>, state: ObjectsState): ObjectsState {
  const [_, stateKey] = findObjectById(state, object.id)

  if (!stateKey) {
    return {
      ...state,
    }
  }

  switch (stateKey) {
    case 'objects': {
      return updateWall(object as Types.Wall, state)
    }
    case 'elevationLines': {
      return updateElevationLine(object as Types.ElevationLine, state)
    }
    case 'segments': {
      return updateSegment(object as Types.WallSegment, state)
    }
    case 'ceilings': {
      return updateCeiling(object as Types.Ceiling, state)
    }
    default: {
      return {
        ...state,
        [stateKey]: {
          ...(state[stateKey] as any),
          [object.id]: {
            ...(state[stateKey] as any)[object.id],
            ...object,
            // In case updated metadata is pushed
            // without other existing metadata,
            // we should retain the existing metadata
            metadata:
              object.metadata || (state[stateKey] as any)[object.id].metadata
                ? {
                    ...(state[stateKey] as any)[object.id].metadata,
                    ...(object.metadata as any),
                  }
                : undefined,
          },
        },
      }
    }
  }
}

function isGlobalAction<T extends object>(action: T): action is T & { globalState: RootState } {
  return 'globalState' in action
}

export type ObjectsState = {
  objects: Record<string, Types.Wall>
  segments: Record<string, Types.WallSegment>
  obstructions: Record<string, Types.Obstruction>
  comfortZones: Record<string, Types.ComfortZone>
  dimensions: Record<string, Types.Dimension>
  roofs: Record<string, Types.Roof>
  doors: Record<string, Types.Door>
  utilityBoxes: Record<string, Types.UtilityBox>
  elevationPoints: Record<string, Types.ElevationPoint>
  elevationLines: Record<string, Types.ElevationLine>
  roofSections: Record<string, Types.RoofSection>
  products: Record<string, Types.Product>
  ceilings: Record<string, Types.Ceiling>
  units: 'INCHES'
  airflow: {
    isFetching: boolean
    isValid: boolean
    data?: Types.Airflow[]
    selectedLayer?: Types.Airflow
  }
  backgroundImage: {
    BACKGROUND_IMAGE: Types.BackgroundImage | undefined
  }
  heatMap: any
}

const initialState: ObjectsState = {
  objects: {},
  segments: {},
  obstructions: {},
  comfortZones: {},
  dimensions: {},
  roofs: {},
  doors: {},
  utilityBoxes: {},
  elevationPoints: {},
  elevationLines: {},
  roofSections: {},
  products: {},
  units: 'INCHES',
  airflow: {
    isFetching: false,
    isValid: false,
    data: undefined,
    selectedLayer: undefined,
  },
  backgroundImage: {
    [LAYER_KEYS.BACKGROUND_IMAGE]: undefined,
  },
  ceilings: {},
  heatMap: {},
}

function testClass<TKey extends keyof ObjectClassNamesToStateTypes>(
  _object: any,
  testForClass: TKey,
  actualClass: string
): _object is ObjectClassNamesToStateTypes[TKey] {
  return testForClass === actualClass
}

export default function objectsReducer(state: ObjectsState = initialState, action: UnknownAction): ObjectsState {
  const newState = {
    ...state,
    utilityBoxes: produce(state.utilityBoxes, draft => utilityBoxReducer(draft, state, action)),
  }

  return generalObjectsReducer(newState, action)
}

function generalObjectsReducer(state: ObjectsState, action: UnknownAction): ObjectsState {
  switch (true) {
    case loadFacility.match(action): {
      return {
        ...state,
        ...omit(omitBy(action.payload.data, isNil), 'cfd'),
        airflow: {
          isFetching: false,
          isValid: false,
          data: action.payload.data.airflow,
        },
        heatMap: {
          data: action.payload.data.heatMap,
        },
      }
    }
    case resetFacility.match(action): {
      return initialState
    }
    case addObjectAction.match(action): {
      const wall = action.payload.object

      // Create segment models
      const segments: Record<string, Types.WallSegment> = {}
      wall.segments.forEach((segment: Types.WallSegment) => {
        segments[segment.id] = segment
      })

      const perimeterPoints = getPerimeterPoints(wall.segments).map<Vector3Tuple>(({ x, y, z }) => [x, y, z])

      let roofs = state.roofs
      let roofSections = state.roofSections
      let roofId
      const roofSectionIds = []

      if (wall.isEnclosed && wall.layerKey === LAYER_KEYS.EXTERIOR_WALLS) {
        // Create roof model
        const roof: Types.Roof = {
          id: Util.guid(),
          perimeterPoints,
          elevationLineIds: [],
          elevationPointIds: [],
          height: wall.segments.reduce((max, curr) => Math.max(max, curr.height), 0),
          color: theme.colors.three.objects.roof.default,
        }

        // Create roof section model
        const beamSpacing = 300 // 25ft
        const columnSpacing = 144 // 12ft
        const beamRotation = 0
        const beamOrientation = getBeamOrientation(beamRotation)
        const roofSection: Types.RoofSection = {
          id: Util.guid(),
          wallId: wall.id,
          roofId: roof.id,
          perimeterPoints,
          height: wall.segments.reduce((max, curr) => Math.max(max, curr.height), 0),
          position: wall.position,
          beamSpacing,
          beamWidth: 8,
          beamDepth: 24,
          beamShift: 0,
          beamRotation,
          columnSpacing,
          columnShift: 0,
          hasColumns: false,
          primaryStructuresEnabled: false,
          primaryStructureType: primaryMountingTypes[0].value,
          secondaryStructureType: primaryToSecondaryTypes[primaryMountingTypes[0].value][0].value,
          secondaryStructureDepth: 9,
          structureStyle: structureStyles[0].value,
          secondaryStructureStyle: structureStyles[0].value,
          primaryVisible: true,
          secondaryVisible: true,
          columnsVisible: true,
          columnWidth: 24, // 2 ft
          columnLength: 24,
          isCylinder: false,
          columnNames: Array.from(Array(26))
            .map((_, i) => i + 65)
            .map(x => String.fromCharCode(x)),
          beamNames: Array.from(Array(26)).map((_, i) => i.toString()),
          columnLineModels: getMountingStructurePositions(
            perimeterPoints,
            getOrthoOrientation(beamOrientation),
            columnSpacing
          ),
          beamModels: getMountingStructurePositions(perimeterPoints, beamOrientation, beamSpacing),
        }

        roofId = roof.id
        roofSectionIds.push(roofSection.id)
        roofs = {
          ...state.roofs,
          [roof.id]: roof,
        }

        roofSections = {
          ...state.roofSections,
          [roofSection.id]: roofSection,
        }
      }

      return {
        ...state,
        objects: {
          ...state.objects,
          [wall.id]: {
            ...wall,
            segments: wall.segments.map((segment: Types.WallSegment) => segment.id),
            roofId,
            roofSectionIds,
          },
        },
        segments: {
          ...state.segments,
          ...segments,
        },
        roofs,
        roofSections,
      }
    }

    case updateObjectAction.match(action): {
      return updateObject(action.payload.object, state)
    }
    case updateObjectsPassively.match(action):
    case updateObjectsAction.match(action): {
      let newState = { ...state }
      action.payload.forEach(object => {
        newState = updateObject(object, newState)
      })
      return newState
    }
    case deleteObjectsAction.match(action) && isGlobalAction(action): {
      let stateCopy = { ...state }

      action.payload.forEach(objectId => {
        const [object, key] = findObjectById(state, objectId)
        const className = objectClassNameFromStateKey(key!)
        let isLocked = false

        if (!object) {
          return
        }

        isLocked = get(
          action.globalState,
          `layers.layers[${LAYER_KEYS[(object as any).layerKey as LayerKeys]}].locked`,
          false
        )

        if (!object || isLocked) {
          return
        }

        // If this is a wall, delete its segments too
        if (testClass(object, CLASS_NAMES.WALL, className)) {
          stateCopy = {
            ...stateCopy,
            objects: omit(stateCopy.objects, [objectId]),
            segments: omit(stateCopy.segments, object.segments),
            roofs: omit(stateCopy.roofs, object.roofId!),
            roofSections: omit(stateCopy.roofSections, object.roofSectionIds),
            doors: omitBy(stateCopy.doors, door => object.segments.includes(door.wallSegmentId)),
          }
        } else if (testClass(object, CLASS_NAMES.WALL_SEGMENT, className)) {
          // If this is a segment update or remove its parent wall
          let newObjects = stateCopy.objects

          const parentWalls = Object.values(newObjects).filter(o => o.segments && o.segments.includes(objectId))
          const parentWall = parentWalls[0]
          if (parentWall) {
            // If the parent wall doesn't have any more segments, delete it
            if (!parentWall.segments || parentWall.segments.length === 1) {
              newObjects = omit(newObjects, [parentWall.id])
            } else {
              newObjects = {
                ...newObjects,
                [parentWall.id]: {
                  ...parentWall,
                  segments: parentWall.segments.filter(id => id !== objectId),
                },
              }
            }
          }

          const windows = parentWall.id in newObjects ? slidingWindow(newObjects[parentWall.id].segments, 2) : []
          let discontinuity = undefined
          for (const [prevID, nextID] of windows) {
            const nextSegment = stateCopy.segments[nextID]
            const prevSegment = stateCopy.segments[prevID]

            if (magnitude(sub(prevSegment.endPoint, nextSegment.startPoint)) >= 0.01) {
              discontinuity = [prevID, nextID]
              break
            }
          }

          // if deleting this segment would make a discontinuity, we need to handle that specially
          if (discontinuity !== undefined) {
            const [prevID, nextID] = discontinuity

            const neighbors = (segmentID: string) => {
              const segment = stateCopy.segments[segmentID]
              const a = newObjects[parentWall.id].segments.find(
                sID => magnitude(sub(segment.endPoint, stateCopy.segments[sID].startPoint)) <= 0.001
              )
              const b = newObjects[parentWall.id].segments.find(
                sID => magnitude(sub(segment.startPoint, stateCopy.segments[sID].endPoint)) <= 0.001
              )
              return [...(a ? [a] : []), ...(b ? [b] : [])]
            }
            const parents = new Map<string, string>()
            const reachable = (() => {
              const queue = []
              const explored = new Set()
              explored.add(prevID)
              queue.push(prevID)
              while (queue.length > 0) {
                const segmentID: string = queue.pop()!
                if (segmentID === nextID) {
                  return true
                }
                for (const neighbor of neighbors(segmentID)) {
                  if (!explored.has(neighbor)) {
                    explored.add(neighbor)
                    queue.push(neighbor)
                    parents.set(neighbor, segmentID)
                  }
                }
              }
              return false
            })()

            if (reachable) {
              // if the discontinuous segments are reachable via other segments, reorder the segments such that there is no gap
              const order = []
              const pop = (map: Map<string, string>, key: string) => {
                const value = map.get(key)
                map.delete(key)
                return value
              }
              for (let child: string | undefined = nextID; child !== undefined; child = pop(parents, child)) {
                order.push(child)
              }
              newObjects = {
                ...newObjects,
                [parentWall.id]: {
                  ...parentWall,
                  segments: order,
                },
              }
            } else {
              // otherwise we need to split into two wall objects
              const oldSegments = newObjects[parentWall.id].segments.slice(
                0,
                newObjects[parentWall.id].segments.indexOf(prevID) + 1
              )
              const newSegments = newObjects[parentWall.id].segments.slice(
                newObjects[parentWall.id].segments.indexOf(nextID)
              )

              const newWallID = Util.guid()

              newObjects = {
                ...newObjects,
                [parentWall.id]: {
                  ...parentWall,
                  segments: oldSegments,
                },
                [newWallID]: {
                  ...parentWall,
                  id: newWallID,
                  segments: newSegments,
                },
              }
              stateCopy = {
                ...stateCopy,
                segments: Object.fromEntries(
                  Object.entries(stateCopy.segments).map(([id, segment]) => [
                    id,
                    { ...segment, ...(newSegments.includes(id) ? { parentId: newWallID } : {}) },
                  ])
                ),
              }
            }
          }

          // Filter out any roof sections without parent walls
          const roofSections: Record<string, Types.RoofSection> = {}
          Object.values(stateCopy.roofSections).forEach(section => {
            if (newObjects[section.wallId]) {
              roofSections[section.id] = section
            }
          })

          // Filter out any roofs without sections
          const roofs: Record<string, Types.Roof> = {}
          Object.values(roofSections).forEach(section => {
            if (stateCopy.roofs[section.roofId]) {
              roofs[section.roofId] = stateCopy.roofs[section.roofId]
            }
          })

          stateCopy = {
            ...stateCopy,
            objects: newObjects,
            roofSections,
            roofs,
            segments: omit(stateCopy.segments, [objectId]),
            doors: omitBy(stateCopy.doors, door => objectId === door.wallSegmentId),
          }
        } else if (testClass(object, CLASS_NAMES.ELEVATION_POINT, className)) {
          // First see if it belongs to a roof
          const parentRoof = Object.values(stateCopy.roofs).find(
            roof => roof.elevationPointIds && roof.elevationPointIds.includes(objectId)
          )

          if (parentRoof) {
            const newRoofs = {
              ...stateCopy.roofs,
              [parentRoof.id]: {
                ...parentRoof,
                elevationPointIds: parentRoof.elevationPointIds.filter(id => id !== objectId),
              },
            }

            stateCopy = {
              ...stateCopy,
              roofs: newRoofs,
              elevationPoints: omit(stateCopy.elevationPoints, [objectId]),
            }
          }

          // Didn't belong to a roof so it must belong to an elevation line
          const parentEL = Object.values(stateCopy.elevationLines).find(
            el => el.elevationPointIds && el.elevationPointIds.includes(objectId)
          )

          if (parentEL) {
            const newElevationLines = {
              ...stateCopy.elevationLines,
              [parentEL.id]: {
                ...parentEL,
                elevationPointIds: parentEL.elevationPointIds.filter(id => id !== objectId),
              },
            }

            stateCopy = {
              ...stateCopy,
              elevationLines: Object.fromEntries(
                Object.entries(newElevationLines).filter(([_, line]) => line.elevationPointIds.length >= 2)
              ),
              elevationPoints: omit(stateCopy.elevationPoints, [objectId]),
            }
          } else {
            stateCopy = {
              ...stateCopy,
              elevationPoints: omit(stateCopy.elevationPoints, [objectId]),
            }
          }
        } else if (testClass(object, CLASS_NAMES.ELEVATION_LINE, className)) {
          const parentRoof = Object.values(stateCopy.roofs).find(roof => roof.elevationLineIds.includes(objectId))

          if (parentRoof) {
            const newRoofs = {
              ...stateCopy.roofs,
              [parentRoof.id]: {
                ...parentRoof,
                elevationLineIds: parentRoof.elevationLineIds.filter(id => id !== objectId),
              },
            }

            stateCopy = {
              ...stateCopy,
              roofs: newRoofs,
              elevationLines: omit(stateCopy.elevationLines, [objectId]),
              elevationPoints: omit(stateCopy.elevationPoints, object.elevationPointIds),
            }
          }
        } else if (testClass(object, CLASS_NAMES.ROOF, className)) {
          stateCopy = {
            ...stateCopy,
          }
        } else if (testClass(object, CLASS_NAMES.ROOF_SECTION, className)) {
          const parentWall = state.objects[object.wallId]
          if (!parentWall) {
            stateCopy = {
              ...stateCopy,
              roofSections: omit(stateCopy.roofSections, [object.id]),
            }
          } else {
            stateCopy = {
              ...stateCopy,
            }
          }
        } else {
          // All other object types delete themselves normally:
          stateCopy = {
            ...stateCopy,
            [objectStateKeyFromClassName(className)]: omit(
              stateCopy[objectStateKeyFromClassName(className) as keyof ObjectsState],
              [objectId]
            ),
          }
        }
      })

      return stateCopy
    }
    case duplicateObjectsAction.match(action): {
      let stateCopy = { ...state }
      action.payload.forEach((newObject: { className: string; id: string; obj: Types.StateObject }) => {
        const stateKey = objectStateKeyFromClassName(newObject.className)
        stateCopy = {
          ...stateCopy,
          [stateKey]: {
            ...stateCopy[stateKey as keyof ObjectsState],
            [newObject.id]: {
              ...newObject.obj,
            },
          },
        }
      })

      return stateCopy
    }
    case duplicateGrid.match(action): {
      let stateCopy = { ...state }
      action.payload.objects.forEach((newObject: any) => {
        const stateKey = objectStateKeyFromClassName(newObject.className)
        if (stateKey === 'products') {
          stateCopy = {
            ...stateCopy,
            [stateKey]: {
              ...stateCopy[stateKey],
              [newObject.id]: {
                cageHeight: newObject.cageHeight,
                className: newObject.className,
                canMountOnColumn: newObject.canMountOnColumn,
                canMountOnWall: newObject.canMountOnWall,
                canMountOverhead: newObject.canMountOverhead,
                canStandAlone: newObject.canStandAlone,
                degreesOfFreedom: newObject.degreesOfFreedom,
                height: newObject.height,
                id: newObject.id,
                isDirectionalOverhead: newObject.isDirectionalOverhead,
                isMounted: newObject.isMounted,
                layerKey: newObject.layerKey,
                metadata: newObject.metadata,
                minFloorClearance: newObject.minFloorClearance,
                minObstructionClearance: newObject.minObstructionClearance,
                minProductClearance: newObject.minProductClearance,
                minRoofClearance: newObject.minRoofClearance,
                minWallClearance: newObject.minWallClearance,
                model: newObject.model,
                mountPosition: newObject.mountPosition,
                mountToCeiling: newObject.mountToCeiling,
                mountingOptionAdderId: newObject.mountingOptionAdderId,
                mountingOptionId: newObject.mountingOptionId,
                pedestals: newObject.pedestals,
                position: newObject.position,
                product: newObject.product,
                recommendedFloorClearance: newObject.recommendedFloorClearance,
                recommendedObstructionClearance: newObject.recommendedObstructionClearance,
                recommendedProductClearance: newObject.recommendedProductClearance,
                recommendedRoofClearance: newObject.recommendedRoofClearance,
                recommendedWallClearance: newObject.recommendedWallClearance,
                rotation: newObject.rotation,
                size: newObject.size,
                tiltEnd: newObject.tiltEnd,
                tiltStart: newObject.tiltStart,
                tiltStep: newObject.tiltStep,
                tubeLength: newObject.tubeLength,
                variationId: newObject.variationId,
                voltage: newObject.voltage,
                voltageId: newObject.voltageId,
              },
            },
          }
        } else if (stateKey === 'obstructions') {
          stateCopy = {
            ...stateCopy,
            [stateKey]: {
              ...stateCopy[stateKey],
              [newObject.id]: {
                className: newObject.className,
                height: newObject.height,
                id: newObject.id,
                length: newObject.length,
                layerKey: newObject.layerKey,
                obstructionType: newObject.obstructionType,
                offset: newObject.offset,
                position: newObject.position,
                positions: newObject.positions,
                resizable: newObject.resizable,
                rotation: newObject.rotation,
                scale: newObject.scale,
                startLocation: newObject.startLocation,
                startPosition: newObject.startPosition,
              },
            },
          }
        } else {
          stateCopy = {
            ...stateCopy,
            [stateKey]: {
              ...stateCopy[stateKey],
              [newObject.id]: {
                ...newObject,
              },
            },
          }
        }
      })

      stateCopy = {
        ...stateCopy,
      }

      return stateCopy
    }
    case updateRoofAction.match(action): {
      return updateObject(action.payload.roof, state)
    }
    case updateRoofStructureAction.match(action): {
      return updateRoofStructure(action.payload, state)
    }
    case updateRoofSectionAction.match(action): {
      const { roofSection } = action.payload
      const newState = updateObject(roofSection, state)
      const currentDepth = state.roofSections[roofSection.id]?.secondaryStructureDepth
      if ('secondaryStructureDepth' in roofSection && currentDepth != null) {
        const productHeightDelta = currentDepth - roofSection.secondaryStructureDepth
        const updatedProducts = Object.values(state.products).map(product => {
          const isOverhead = product.isDirectionalOverhead || product.layerKey === 'PRODUCTS_OVERHEAD'
          return [
            product.id,
            {
              ...product,
              position: {
                ...product.position,
                z: product.position.z + (isOverhead ? productHeightDelta : 0),
              },
            },
          ]
        })
        newState.products = Object.fromEntries(updatedProducts)
      }
      return newState
    }
    case addMountingBeam.match(action): {
      const roofSection = state.roofSections[action.payload.roofSectionId]
      const updatedBeamModels = roofSection.beamModels!.slice()
      if (action.payload.previousBeamId) {
        const prevBeamIndex = roofSection.beamModels!.findIndex(beam => beam.id === action.payload.previousBeamId)
        updatedBeamModels.splice(prevBeamIndex + 1, 0, {
          id: action.payload.beamId,
          position: action.payload.beamPosition,
        })
      } else {
        updatedBeamModels.push({
          id: action.payload.beamId,
          position: action.payload.beamPosition,
        })
      }
      return {
        ...state,
        roofSections: {
          ...state.roofSections,
          [roofSection.id]: {
            ...roofSection,
            beamModels: updatedBeamModels,
          },
        },
      }
    }
    case removeMountingBeam.match(action): {
      const roofSection = state.roofSections[action.payload.roofSectionId]
      const updatedBeamModels = roofSection.beamModels!.slice()
      const beamIndex = roofSection.beamModels!.findIndex(beam => beam.id === action.payload.beamId)
      updatedBeamModels.splice(beamIndex, 1)
      return {
        ...state,
        roofSections: {
          ...state.roofSections,
          [roofSection.id]: {
            ...roofSection,
            beamModels: updatedBeamModels,
          },
        },
      }
    }
    case updateMountingBeam.match(action): {
      const { roofSectionId, beamId, beamPosition, direction } = action.payload

      const roofSection = state.roofSections[roofSectionId]
      const beam = roofSection.beamModels!.find(beam => beam.id === beamId)!

      let updatedBeamModels = roofSection.beamModels!.slice()
      const beamIndex = findIndex(roofSection.beamModels, beamModel => beamModel.id === beamId)

      // How much to shift each line
      const beamPositionDiff = {
        x: beamPosition.x - beam.position.x,
        y: beamPosition.y - beam.position.y,
      }

      updatedBeamModels = shiftGridLines({
        models: updatedBeamModels,
        rotation: Math.sin(roofSection.beamRotation),
        spacing: roofSection.beamSpacing,
        startIndex: beamIndex,
        positionDiff: beamPositionDiff,
        direction,
      })

      return {
        ...state,
        roofSections: {
          ...state.roofSections,
          [roofSection.id]: {
            ...roofSection,
            beamModels: updatedBeamModels,
          },
        },
      }
    }
    case addColumnLine.match(action): {
      const roofSection = state.roofSections[action.payload.roofSectionId]
      const updatedColumnLineModels = roofSection.columnLineModels!.slice()
      if (action.payload.previousColumnLineId) {
        const prevColumnLineIndex = roofSection.columnLineModels!.findIndex(
          columnLine => columnLine.id === action.payload.previousColumnLineId
        )
        updatedColumnLineModels.splice(prevColumnLineIndex + 1, 0, {
          id: action.payload.columnLineId,
          position: action.payload.columnLinePosition,
        })
      } else {
        updatedColumnLineModels.push({
          id: action.payload.columnLineId,
          position: action.payload.columnLinePosition,
        })
      }
      return {
        ...state,
        roofSections: {
          ...state.roofSections,
          [roofSection.id]: {
            ...roofSection,
            columnLineModels: updatedColumnLineModels,
          },
        },
      }
    }
    case removeColumnLine.match(action): {
      const roofSection = state.roofSections[action.payload.roofSectionId]
      const updatedColumnLineModels = roofSection.columnLineModels.slice()
      const columnLineIndex = roofSection.columnLineModels.findIndex(
        columnLine => columnLine.id === action.payload.columnLineId
      )
      updatedColumnLineModels.splice(columnLineIndex, 1)
      return {
        ...state,
        roofSections: {
          ...state.roofSections,
          [roofSection.id]: {
            ...roofSection,
            columnLineModels: updatedColumnLineModels,
          },
        },
      }
    }
    case updateColumnLine.match(action): {
      const { roofSectionId, columnLineId, columnLinePosition, direction } = action.payload

      const roofSection = state.roofSections[roofSectionId]
      const columnLine = roofSection.columnLineModels.find(columnLine => columnLine.id === columnLineId)!

      let updatedColumnLineModels = roofSection.columnLineModels.slice()
      const columnLineIndex = findIndex(roofSection.columnLineModels, columnLine => columnLine.id === columnLineId)

      // How much to shift each line
      const columnLinePositionDiff = {
        x: columnLinePosition.x - columnLine.position.x,
        y: columnLinePosition.y - columnLine.position.y,
      }

      updatedColumnLineModels = shiftGridLines({
        models: updatedColumnLineModels,
        rotation: Math.cos(roofSection.beamRotation),
        spacing: roofSection.columnSpacing,
        startIndex: columnLineIndex,
        positionDiff: columnLinePositionDiff,
        direction,
      })

      return {
        ...state,
        roofSections: {
          ...state.roofSections,
          [roofSection.id]: {
            ...roofSection,
            columnLineModels: updatedColumnLineModels,
          },
        },
      }
    }
    case revertRoofSections.match(action): {
      if (!action.payload.id) {
        return {
          ...state,
        }
      }
      const parentWallId = state.roofSections[action.payload.id].wallId
      const parentWall = state.objects[parentWallId]
      const roofSectionIds = state.objects[parentWallId].roofSectionIds
      const firstSectionId = roofSectionIds[0]

      const segmentObjects = parentWall.segments.map(id => state.segments[id])
      const perimeterPoints = getPerimeterPoints(segmentObjects).map<Vector3Tuple>(({ x, y, z }) => [x, y, z])

      return {
        ...state,
        objects: {
          ...state.objects,
          [parentWallId]: {
            ...state.objects[parentWallId],
            roofSectionIds: [firstSectionId],
          },
        },
        roofSections: {
          ...omit(state.roofSections, roofSectionIds),
          [firstSectionId]: {
            ...state.roofSections[firstSectionId],
            perimeterPoints,
          },
        },
      }
    }
    case addElevationPoint.match(action): {
      return {
        ...state,
        elevationPoints: {
          ...state.elevationPoints,
          [action.payload.elevationPoint.id]: {
            ...action.payload.elevationPoint,
          },
        },
      }
    }
    case updateElevationPointAction.match(action): {
      return updateObject(action.payload.elevationPoint, state)
    }
    case addElevationLine.match(action): {
      const roof = state.roofs[action.payload.elevationLine.roofId]
      const elevationPointsArray = action.payload.elevationPoints
      const elevationPointsObj: Record<string, Types.ElevationPoint> = {}
      const roofLineIds = get(roof, 'elevationLineIds', [])

      elevationPointsArray.forEach(ep => (elevationPointsObj[ep.id] = ep))

      return {
        ...state,
        elevationLines: {
          ...state.elevationLines,
          [action.payload.elevationLine.id]: {
            ...action.payload.elevationLine,
          },
        },
        elevationPoints: {
          ...state.elevationPoints,
          ...elevationPointsObj,
        },
        roofs: {
          ...state.roofs,
          [roof.id]: {
            ...roof,
            elevationLineIds: [...roofLineIds, action.payload.elevationLine.id],
          },
        },
      }
    }
    case updateElevationLineAction.match(action): {
      return updateObject(action.payload.elevationLine, state)
    }
    case updateWallAction.match(action): {
      return updateObject(action.payload.wall, state)
    }
    case updateSegmentsAction.match(action): {
      const newSegments = action.payload.ids.map(id => ({
        ...state.segments[id],
        ...action.payload.updates,
      }))

      const roofSections: Record<string, Types.RoofSection> = {}
      const roofs: Record<string, Types.Roof> = {}
      const segmentIds = new Set(action.payload.ids)
      const walls = uniqBy(newSegments, 'parentId').map(s => state.objects[s.parentId])
      walls.forEach(wall => {
        const isExteriorWall = wall.layerKey === 'EXTERIOR_WALLS'
        if (isExteriorWall) {
          const heightUpdate = action.payload.updates.height
          wall.segments.forEach(id => {
            const isSiblingSegmentThatNeedsHeightUpdate = !segmentIds.has(id) && heightUpdate
            if (!isSiblingSegmentThatNeedsHeightUpdate) return
            newSegments.push({
              ...state.segments[id],
              height: heightUpdate,
            })
          })
        }

        wall.roofSectionIds.forEach(roofSectionId => {
          roofSections[roofSectionId] = {
            ...state.roofSections[roofSectionId],
            ...pick(action.payload.updates, ['height']),
          }
        })
        // Interior walls don't have roofs,
        // So only update the roof if there is a roofId
        if (wall.roofId && state.roofs[wall.roofId]) {
          roofs[wall.roofId] = {
            ...state.roofs[wall.roofId],
            ...pick(action.payload.updates, ['height']),
          }
        }
      })

      return {
        ...state,
        roofSections: {
          ...state.roofSections,
          ...roofSections,
        },
        roofs: {
          ...state.roofs,
          ...roofs,
        },
        segments: {
          ...state.segments,
          ...keyBy(newSegments, 'id'),
        },
      }
    }
    case addDoor.match(action): {
      return {
        ...state,
        doors: {
          ...state.doors,
          [action.payload.door.id]: {
            ...action.payload.door,
          },
        },
      }
    }
    case updateDoor.match(action): {
      return updateObject(action.payload.door, state)
    }
    case addUtilityBoxAction.match(action): {
      return {
        ...state,
        utilityBoxes: {
          ...state.utilityBoxes,
          [action.payload.utilityBox.id]: {
            ...action.payload.utilityBox,
          },
        },
      }
    }
    case updateUtilityBox.match(action): {
      return updateObject(action.payload.utilityBox, state)
    }
    case addProductAction.match(action): {
      return {
        ...state,
        products: {
          ...state.products,
          [action.payload.product.id]: {
            ...action.payload.product,
            includedInQuote: true,
          },
        },
      }
    }
    case distributeProductsAction.match(action): {
      const products: Record<string, Types.Product> = {}
      action.payload.models.forEach(model => {
        products[model.id] = {
          ...model,
        }
      })
      return {
        ...state,
        products: {
          ...state.products,
          ...products,
        },
      }
    }
    case updateProductAction.match(action): {
      return updateObject(action.payload.product, state)
    }
    case addObstructionAction.match(action): {
      return {
        ...state,
        obstructions: {
          ...state.obstructions,
          [action.payload.obstruction.id]: {
            ...action.payload.obstruction,
            id: action.payload.obstruction.id,
            layerKey: LAYER_KEYS.OBSTRUCTIONS,
          },
        },
      }
    }
    case distributeObstructionsAction.match(action): {
      const obstructions: Record<string, Types.Obstruction> = {}
      action.payload.models.forEach(model => {
        obstructions[model.id] = {
          ...model,
        }
      })
      return {
        ...state,
        obstructions: {
          ...state.obstructions,
          ...obstructions,
        },
      }
    }
    case distributeDoors.match(action): {
      const doors: Record<string, Types.Door> = {}
      action.payload.models.forEach(model => {
        doors[model.id] = {
          ...model,
        }
      })
      return {
        ...state,
        doors: {
          ...state.doors,
          ...doors,
        },
      }
    }
    case updateObstructionAction.match(action): {
      return updateObject(action.payload.obstruction, state)
    }
    case addComfortZone.match(action): {
      return {
        ...state,
        comfortZones: {
          ...state.comfortZones,
          [action.payload.comfortZone.id]: {
            ...action.payload.comfortZone,
            name: `Zone ${Object.keys(state.comfortZones).length + 1}`,
            id: action.payload.comfortZone.id,
          },
        },
      }
    }
    case updateComfortZone.match(action): {
      return updateObject(action.payload.comfortZone, state)
    }
    case action.type === CFD.UPLOAD_COMPLETE: {
      const comfortZonesArray = Object.entries(state.comfortZones)
      const comfortZones: Record<string, Types.ComfortZone> = {}
      comfortZonesArray.forEach(([id, comfortZone]) => {
        comfortZones[id] = {
          ...comfortZone,
        }
      })
      return {
        ...state,
        comfortZones,
      }
    }
    case addDimensionAction.match(action): {
      return {
        ...state,
        dimensions: {
          ...state.dimensions,
          [action.payload.dimension.id]: {
            ...action.payload.dimension,
            id: action.payload.dimension.id,
            dimensionFinished: action.payload.dimension.dimensionFinished,
            startPos: action.payload.dimension.startPos,
            endPos: action.payload.dimension.endPos,
          },
        },
      }
    }
    case updateDimension.match(action): {
      return updateObject(action.payload.dimension, state)
    }
    case addCeilingAction.match(action): {
      return {
        ...state,
        ceilings: {
          ...state.ceilings,
          [action.payload.ceiling.id]: {
            ...action.payload.ceiling,
          },
        },
      }
    }
    case updateCeilingAction.match(action): {
      return updateObject(action.payload.ceiling, state)
    }
    case deleteCeilingAction.match(action): {
      return {
        ...state,
        ceilings: omit(state.ceilings, [action.payload.ceiling.id]),
      }
    }
    case requestAirflow.match(action): {
      return {
        ...state,
        airflow: {
          ...state.airflow,
          isFetching: true,
          isValid: false,
        },
      }
    }
    case receiveAirflow.match(action): {
      return {
        ...state,
        airflow: {
          isFetching: false,
          isValid: true,
          data: action.payload,
          selectedLayer: state.airflow.selectedLayer || (action.payload && action.payload[3]),
        },
      }
    }
    case updateAirflowStatus.match(action): {
      return {
        ...state,
        airflow: {
          ...state.airflow,
          isValid: action.payload,
        },
      }
    }
    case setAirflowLayer.match(action): {
      if (!state.airflow.data)
        return {
          ...state,
        }

      const selected = state.airflow.data.filter((d: any) => {
        return d.evaluationHeight === action.payload.layerKey
      })
      return {
        ...state,
        airflow: {
          ...state.airflow,
          selectedLayer: selected[0],
        },
      }
    }
    case addBackgroundImage.match(action): {
      return {
        ...state,
        backgroundImage: action.payload.backgroundImage,
      }
    }
    case deleteBackgroundImage.match(action): {
      return {
        ...state,
        backgroundImage: initialState.backgroundImage,
      }
    }
    case updateBackgroundImage.match(action): {
      return {
        ...state,
        backgroundImage: {
          [LAYER_KEYS.BACKGROUND_IMAGE]: {
            ...state.backgroundImage[LAYER_KEYS.BACKGROUND_IMAGE],
            ...action.payload.backgroundImage,
          } as Types.BackgroundImage,
        },
      }
    }
    case toggleLayerVisibility.match(action): {
      const wallLayers = ['INTERIOR_WALLS', 'EXTERIOR_WALLS']
      const isWallLayer = wallLayers.includes(action.payload.layerKey)
      if (!isWallLayer) return { ...state }

      if (isEmpty(state.doors) && isEmpty(state.utilityBoxes)) return { ...state }

      const doors: Record<string, Types.Door> = {}
      const utilityBoxes: Record<string, Types.UtilityBox> = {}
      if (!isEmpty(state.doors)) {
        Object.keys(state.doors).forEach(key => {
          doors[key] = {
            ...state.doors[key],
            // updateId: Util.guid(),
          }
        })
      }
      if (!isEmpty(state.utilityBoxes)) {
        Object.keys(state.utilityBoxes).forEach(key => {
          utilityBoxes[key] = {
            ...state.utilityBoxes[key],
            // updateId: Util.guid(),
          }
        })
      }

      return {
        ...state,
        doors: {
          ...doors,
        },
        utilityBoxes: {
          ...utilityBoxes,
        },
      }
    }
    case addMetadataImage.match(action): {
      const { objectId, cloudinaryId, width, height } = action.payload

      const [object, _key] = findObjectById(state, objectId)

      if (!object) {
        return {
          ...state,
        }
      }

      const newObject = {
        ...object,
        metadata: {
          ...object.metadata!,
          images: [
            ...get(object, 'metadata.images', []),
            {
              cloudinaryId,
              width,
              height,
            },
          ],
        },
      }

      return updateObject(newObject, state)
    }
    case requestHeatMap.match(action): {
      return {
        ...state,
        heatMap: {
          isFetching: true,
          isValid: false,
          ...state.heatMap,
        },
      }
    }
    case receiveHeatMap.match(action): {
      return {
        ...state,
        heatMap: {
          isFetching: false,
          isValid: true,
          data: action.payload,
          selectedLayer: action.payload && action.payload[0],
        },
      }
    }
    case updateHeatMapStatus.match(action): {
      return {
        ...state,
        heatMap: {
          ...state.heatMap,
          isValid: action.payload,
        },
      }
    }
    case setHeatMapLayer.match(action): {
      if (!state.heatMap.data)
        return {
          ...state,
        }

      const selected = state.heatMap.data.filter((d: any) => {
        return d.evaluationHeight === action.payload.layerKey
      })
      return {
        ...state,
        heatMap: {
          ...state.heatMap,
          selectedLayer: selected[0],
        },
      }
    }
    default: {
      return state
    }
  }
}
